import { DatePipe } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelect, MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxCurrencyConfig, NgxCurrencyDirective } from 'ngx-currency';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { Subscription } from 'rxjs';

import { SelectPesquisavelComponent } from '../../../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { ClienteDetalheStruct } from '../../../../../shared/services/structs/vendas/cliente-detalhe.struct';
import { HistoricoClienteStruct } from '@shared/services/structs/vendas/historico-cliente.struct';
import { KanbanCor } from '../../../../../shared/services/models/vendas/kanban-cor.model';
import { VdClienteComponent } from '../vd-cliente/vd-cliente.component';
import { VdEnderecoEntregaComponent } from '../vd-endereco-entrega/vd-endereco-entrega.component';
import { EtapaKanbanStruct } from '../../../../../shared/services/structs/vendas/etapa-kanban.struct';
import { PvCorVm } from '@shared/helpers/kanban-cor.helper';
import { environment } from '../../../../../../environments/environment';
import { MaskService, Masks } from '../../../../../shared/services/mask.service';
import { AlertService, AlertType } from '../../../../../shared/services/alert.service';
import { MotorFiscalLookupService } from '../../../../../shared/services/apis/motor-fiscal/motor-fiscal-lookup.service';
import { LookupService as ProdutoLookupService } from '../../../../../shared/services/apis/produto/produto-lookup.service';
import { ConfigOrcamento } from '../../../../../shared/services/models/vendas/config-orcamento.model';
import { ConfigPedidoVenda } from '../../../../../shared/services/models/vendas/config-pedido-venda.model';
import { ConfigVendasGeral } from '../../../../../shared/services/models/vendas/config-vendas-geral.model';
import { ClienteVendaSelectStruct } from '../../../../../shared/services/structs/pessoa/cliente-venda-select.struct';
import { LookupItemStruct as LookupItemPessoaStruct } from '../../../../../shared/services/structs/pessoa/lookup-item.struct';
import { LookupItemStruct } from '../../../../../shared/services/structs/motor-fiscal/lookup-item.struct';
import { VendedorVendaStruct } from '../../../../../shared/services/structs/vendas/vendedor-venda.struct';
import { TipoNumeracaoDocumentoEnum } from '../../../../../shared/enums/vendas/tipo-numeracao-documento.enum';
import { TipoOperacaoEnum } from '../../../../../shared/enums/motor-fiscal/tipo-operacao.enum';

/** Código do leiaute (indIntermed) da operação com intermediador: exige CNPJ e identificador. */
const CODIGO_COM_INTERMEDIADOR = '1';

/**
 * Seção 1 - Dados gerais, com as subseções Documento, Prazos, Cliente e Endereço de entrega (a
 * mesma organização da NF-e de saída). O título e o recolher da seção são do shell.
 *
 * Prazos muda por processo: o orçamento tem a validade da proposta (regra da configuração, abaixo)
 * e a descrição do prazo; o pedido tem as datas de envio e de despacho. A data prevista de entrega
 * é a mesma coluna nos dois.
 *
 * As listas vêm de APIs diferentes e cada uma tem o seu jeito:
 *   cliente ................. busca ONLINE na API.Pessoa (a base é grande demais para carregar inteira)
 *   naturezas ............... lookup do MotorFiscal, carregado na abertura
 *   indicador intermediador . lookup do MotorFiscal (indIntermed), carregado na abertura
 *   listas de preço ......... lookup da API.Produto, carregado na abertura
 *   vendedores .............. já vêm no agregado, filtrados pelo escopo do usuário
 *
 * Todos são endpoints só-JWT: os equivalentes das telas de cadastro exigem permissão de
 * configuração, que um vendedor não tem.
 */
/** Um destino de "Mover para": a etapa, a legenda abaixo do nome e o visual do catálogo do Kanban. */
export interface DestinoDeEtapa {
    etapa: EtapaKanbanStruct;
    /** "Exige motivo" quando o destino pede motivo; senão o nome do processo (Orçamento, Pedido de venda). */
    legenda: string;
    cor: PvCorVm;
    icone: string | null;
}

@Component({
    selector: 'app-vd-dados',
    standalone: true,
    imports: [
        DatePipe,
        ReactiveFormsModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatDatepickerModule,
        MatIconModule,
        MatSelectModule,
        MatTooltipModule,
        MatSlideToggleModule,
        MatProgressSpinnerModule,
        NgxCurrencyDirective,
        NgxMaskDirective,
        SelectPesquisavelComponent,
        VdClienteComponent,
        VdEnderecoEntregaComponent,
    ],
    providers: [provideNgxMask()],
    templateUrl: './vd-dados.component.html',
    styleUrl: './vd-dados.component.css',
})
export class VdDadosComponent implements OnInit, OnChanges, OnDestroy {
    @Input({ required: true }) public form!: FormGroup;
    @Input() public ehOrcamento: boolean = true;
    @Input() public vendedores: VendedorVendaStruct[] = [];
    @Input() public configVendasGeral: ConfigVendasGeral | null = null;
    @Input() public configOrcamento: ConfigOrcamento | null = null;
    @Input() public configPedidoVenda: ConfigPedidoVenda | null = null;
    @Input() public isSomenteLeitura: boolean = false;

    /** Numero do documento e validade sao inteiros: a mascara vem da tela (frontend.md: sem type=number). */
    @Input({ required: true }) public mascaraInteira!: Partial<NgxCurrencyConfig>;

    /** `datetimeInclusao` do documento gravado, em leitura no Documento. */
    @Input() public cadastradoEm: string | null = null;

    /** So de passagem: quem usa e o modal do cliente, para pintar a etapa das vendas. */
    @Input() public coresKanban: KanbanCor[] = [];

    // ---- Etapa (§9, protótipo): só em documento existente. O shell resolve etapa, visual e
    // os destinos permitidos (que vêm do SERVIDOR no GET, a mesma régua do board e do POST
    // /etapa) e é quem move; aqui o campo só lista e devolve a escolha por `moverEtapa`.
    @Input() public ehNovo: boolean = true;
    @Input() public etapaAtual: EtapaKanbanStruct | null = null;
    @Input() public corEtapa: PvCorVm | null = null;
    @Input() public iconeEtapa: string | null = null;
    @Input() public destinosDeEtapa: DestinoDeEtapa[] = [];

    /** Enquanto o movimento anterior não voltou do servidor, o campo não aceita outro. */
    @Input() public isMovendoEtapa: boolean = false;

    /**
     * Pedido do menu Ações do shell para abrir o "Mover para" (task 148619). É um contador: cada
     * clique no menu muda o valor, e a mudança é o sinal — o shell não mexe no select daqui.
     */
    @Input() public pedidoAbrirEtapa: number = 0;

    @ViewChild('selectEtapa') private selectEtapa?: MatSelect;
    @ViewChild('campoEtapa', { read: ElementRef }) private campoEtapa?: ElementRef<HTMLElement>;

    @Output() public moverEtapa = new EventEmitter<EtapaKanbanStruct>();

    /**
     * O valor é sempre a etapa ATUAL: escolher um destino não troca o campo — troca o documento,
     * e o campo só muda quando o shell recarrega o documento movido (ou fica, se o servidor
     * recusou). Por isso a seleção é desfeita logo depois de emitida. Fora do `form` do documento
     * de propósito: etapa não é campo do request de salvamento.
     */
    public readonly etapaControl = new FormControl<number | null>(null);

    /** A tela avisa quando a lista de preço muda: quem recalcula os itens é a seção de Itens. */
    @Output() public listaPrecoAlterada = new EventEmitter<number | null>();

    /**
     * Nome da lista escolhida, resolvido no lookup desta seção. Sobe para a tela mandar no salvamento
     * (`nomeListaPreco`, só informativo). Emitido ao trocar e quando o lookup chega.
     */
    @Output() public nomeListaPrecoAlterado = new EventEmitter<string | null>();

    /** O financeiro do cliente sobe para a tela, que é quem decide se o documento pode gravar. */
    @Output() public clienteFinanceiro = new EventEmitter<ClienteDetalheStruct | null>();

    /** O usuário ESCOLHEU um cliente na busca (não dispara no carregar): o shell preenche "Aos cuidados de" (§5.1). */
    @Output() public clienteEscolhido = new EventEmitter<ClienteVendaSelectStruct | null>();

    /** "Recalcular preços" ao lado da Lista de preço (§4.1): os itens voltam ao preço da lista vigente. */
    @Output() public recalcularPrecos = new EventEmitter<void>();

    /** As últimas compras do cliente sobem para a tela, que aplica a tag "1ª Venda". */
    @Output() public historicoCliente = new EventEmitter<HistoricoClienteStruct | null>();

    /**
     * Cliente com título vencido barra o PEDIDO DE VENDA, e só quando a empresa configurou assim
     * (`config_pedido_venda.is_bloquear_cliente_atraso`). Orçamento nunca barra: ele é uma
     * proposta, e propor a quem deve é decisão comercial, não financeira.
     */
    public get bloqueiaClienteEmAtraso(): boolean {
        return !this.ehOrcamento && this.configPedidoVenda?.isBloquearClienteAtraso === true;
    }

    public readonly masks: Masks = MaskService.getMasks();

    public naturezas: LookupItemStruct[] = [];
    public indicadoresIntermediador: LookupItemStruct[] = [];
    public listasPreco: LookupItemPessoaStruct[] = [];
    public isCarregandoListasPreco: boolean = false;
    public isCarregandoNaturezas: boolean = false;

    private assinaturaIntermediador?: Subscription;

    /** O cliente é buscado ONLINE: o select monta a URL e pesquisa conforme o usuário digita. */
    public readonly urlBuscaCliente = `${environment.urlApiPessoa}Lookup/listPessoaSelect`;

    constructor(
        private alertService: AlertService,
        private motorFiscalLookupService: MotorFiscalLookupService,
        private produtoLookupService: ProdutoLookupService,
    ) {}

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['pedidoAbrirEtapa'] && !changes['pedidoAbrirEtapa'].firstChange) {
            // Depois do menu fechar: abrir o select no mesmo ciclo do clique o fecharia de novo.
            setTimeout(() => {
                this.campoEtapa?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                this.selectEtapa?.open();
            });
        }

        if (changes['etapaAtual']) this.etapaControl.setValue(this.etapaAtual?.idEtapaKanban ?? null, { emitEvent: false });

        if (changes['destinosDeEtapa'] || changes['isMovendoEtapa'] || changes['etapaAtual']) {
            // Sem destino o campo continua abrindo: a lista é que informa (§9). Só trava enquanto move.
            if (!this.isMovendoEtapa) this.etapaControl.enable({ emitEvent: false });
            else this.etapaControl.disable({ emitEvent: false });
        }

        if (changes['ehOrcamento']) this.aplicarIntermediador();
    }

    public aoEscolherEtapa(evento: MatSelectChange): void {
        const destino = this.destinosDeEtapa.find((d) => d.etapa.idEtapaKanban === evento.value);

        this.etapaControl.setValue(this.etapaAtual?.idEtapaKanban ?? null, { emitEvent: false });

        if (destino) this.moverEtapa.emit(destino.etapa);
    }

    public ngOnInit(): void {
        this.carregarNaturezas();
        this.carregarListasPreco();
        this.carregarIndicadoresIntermediador();

        this.assinaturaIntermediador = this.indicadorIntermediadorControl?.valueChanges.subscribe(() => this.aplicarIntermediador());
    }

    public ngOnDestroy(): void {
        this.assinaturaIntermediador?.unsubscribe();
    }

    /**
     * O cliente já gravado, para o card abrir preenchido em vez de mostrar a busca vazia num
     * documento que tem cliente. Vem do shell, que o recebe no GET.
     */
    @Input() public clienteSelecionado: ClienteVendaSelectStruct | null = null;

    /** Documento ligado a uma oportunidade do CRM: o cliente fica travado e o motivo vai no tooltip. */
    @Input() public motivoClienteBloqueado: string | null = null;

    /** O painel de endereço só existe com o toggle ligado (§5.3). */
    public get entregaEmOutroEndereco(): boolean {
        return this.form.get('isEntregaEnderecoDiferente')?.value === true;
    }

    public get clienteControl(): FormControl {
        return this.form.get('idPessoaCliente') as FormControl;
    }

    public get vendedorControl(): FormControl {
        return this.form.get('idVendedor') as FormControl;
    }

    public get naturezaControl(): FormControl {
        return this.form.get('idNatOp') as FormControl;
    }

    public get listaPrecoControl(): FormControl {
        return this.form.get('idListaPreco') as FormControl;
    }

    public get indicadorIntermediadorControl(): FormControl {
        return this.form.get('idIndicadorIntermediador') as FormControl;
    }

    /** Só a operação com intermediador (indIntermed 1) pede CNPJ e identificador. */
    public get isComIntermediador(): boolean {
        const id = this.indicadorIntermediadorControl?.value;

        return this.indicadoresIntermediador.find((i) => i.id === id)?.codigo === CODIGO_COM_INTERMEDIADOR;
    }

    /**
     * CNPJ e identificador só valem na operação com intermediador (indIntermed 1): no pedido, nela
     * são obrigatórios; fora dela são limpos, para o valor escondido não ir para o pedido nem para a
     * nota. Sem a lista carregada não decide, para não apagar o que veio gravado.
     */
    private aplicarIntermediador(): void {
        if (!this.form || this.indicadoresIntermediador.length === 0) return;

        const comIntermediador = this.isComIntermediador;
        const exigido = !this.ehOrcamento && comIntermediador;

        for (const nome of ['cnpjIntermediador', 'identificadorIntermediador']) {
            const controle = this.form.get(nome);

            if (!controle) continue;

            if (!comIntermediador && controle.value != null) controle.setValue(null, { emitEvent: false });

            if (exigido) {
                controle.addValidators(Validators.required);
            } else {
                controle.removeValidators(Validators.required);
            }

            controle.updateValueAndValidity({ emitEvent: false });
        }
    }

    /**
     * VALIDADE do orçamento: tem duas caras, e quem decide é a configuração (story 2 do Orçamento):
     *
     *   "Orçamentos expiram automaticamente" LIGADO  -> o prazo é o da configuração; o campo só é
     *                                                   editável com "permitir editar" ligado, e
     *                                                   estourar a validade vira bloqueio (6.5)
     *   DESLIGADO                                    -> campo LIVRE, sem trava e sem badge "Vencido"
     *
     * O campo aparece SEMPRE no orçamento (§4.2 o põe como obrigatório); o que a configuração
     * decide é se ele é editável. `validadeDias` é a ENTRADA; a data de validade é derivada dela e
     * da emissão pelo backend, e a tela mostra a data resultante só como apoio.
     */
    public get expiraAutomatico(): boolean {
        return this.configOrcamento?.isOrcamentoExpiraAutomatico === true;
    }

    /**
     * Travado só quando a EXPIRAÇÃO está ligada e a configuração não permite editar. Com a
     * expiração desligada, `isPrazoValidadeEditavel` vem gravado como false pela tela de
     * configurações - e não significa nada: não há prazo a proteger.
     */
    public get validadeBloqueada(): boolean {
        return this.isSomenteLeitura || (this.expiraAutomatico && this.configOrcamento?.isPrazoValidadeEditavel === false);
    }

    /**
     * A data que a validade produz, só para o vendedor ver o efeito do prazo. Calculada a partir
     * da emissão do próprio form - não do relógio do navegador, que daria outro dia quando o
     * documento é antigo.
     */
    public get dataValidadePrevista(): Date | null {
        const emissao = this.form?.get('dataEmissao')?.value;
        const dias = Number(this.form?.get('validadeDias')?.value);

        if (!emissao || !isFinite(dias)) return null;

        const base = new Date(emissao);

        if (isNaN(base.getTime())) return null;

        base.setDate(base.getDate() + dias);

        return base;
    }

    /**
     * §4.2: o número só é editável com numeração MANUAL. Na sequencial quem decide é o servidor,
     * no salvamento — e mostrar um campo editável ali seria prometer o que a tela não cumpre.
     */
    public get isNumeroEditavel(): boolean {
        return this.configVendasGeral?.idTipoNumeracaoDocumento === TipoNumeracaoDocumentoEnum.Manual;
    }

    /**
     * §4.2: a natureza não é renderizada com o cálculo de impostos desligado — e, ligado, é
     * obrigatória. Cada processo tem a sua configuração.
     */
    public get exibeNatureza(): boolean {
        return this.ehOrcamento
            ? this.configOrcamento?.calcularImpostos === true
            : this.configPedidoVenda?.isCalcularImpostos === true;
    }

    /** §4.3: a configuração é de CADA processo, e não há herança entre eles na conversão. */
    public get vendedorObrigatorio(): boolean {
        return this.ehOrcamento
            ? this.configOrcamento?.vendedorObrigatorio === true
            : this.configPedidoVenda?.isVendedorObrigatorio === true;
    }

    /**
     * O botão redondo ao lado da natureza (§4.1, fileira 1). Existe porque a natureza é cadastrada
     * em OUTRA tela, no módulo fiscal: quem acabou de criar uma precisa vê-la aqui sem recarregar
     * o documento inteiro e perder o que digitou.
     */
    public recarregarNaturezas(): void {
        this.carregarNaturezas(true);
    }

    private carregarNaturezas(avisar: boolean = false): void {
        this.isCarregandoNaturezas = true;

        // Só naturezas de SAÍDA: documento comercial não gera entrada, e sem o filtro o campo
        // oferecia "Devolução de compra" e "Entrada de mercadorias para revenda" num pedido de
        // venda. O modal do item já filtrava assim (`getVendaItemLookup`) — o cabeçalho
        // é que tinha ficado para trás, e os dois precisam oferecer o mesmo universo.
        this.motorFiscalLookupService.getNaturezaOperacaoLookup(TipoOperacaoEnum.Saida).subscribe({
            next: (lista) => {
                this.naturezas = lista;
                this.isCarregandoNaturezas = false;

                if (avisar) {
                    this.alertService.show('Naturezas atualizadas', `${lista.length} disponíveis.`, AlertType.success);
                }
            },
            error: () => {
                this.isCarregandoNaturezas = false;
            },
        });
    }

    /** Indicador indIntermed da NF-e: a venda leva o intermediador para a nota. */
    private carregarIndicadoresIntermediador(): void {
        this.motorFiscalLookupService.getIndicadoresIntermediador().subscribe({
            next: (lista) => {
                this.indicadoresIntermediador = lista ?? [];
                this.aplicarIntermediador();
            },
            error: () => {
                this.indicadoresIntermediador = [];
            },
        });
    }

    /** Em outra aba, sem perder o documento: com natureza escolhida abre o cadastro dela; sem, a listagem de naturezas. */
    public abrirNaturezaOperacao(): void {
        const id = this.naturezaControl?.value;

        window.open(id ? `/configuracoes/notas-fiscais/natureza-operacao/cadastrar/${id}` : '/configuracoes/notas-fiscais/natureza-operacao', '_blank');
    }

    /**
     * O botao redondo ao lado da lista de preco, gemeo do da natureza: a lista tambem e
     * cadastrada em outra tela, e quem acabou de criar uma precisa ve-la aqui sem recarregar o
     * documento e perder o que digitou.
     */
    public recarregarListasPreco(): void {
        this.carregarListasPreco(true);
    }

    private carregarListasPreco(avisar: boolean = false): void {
        this.isCarregandoListasPreco = true;

        this.produtoLookupService.getListaPrecoSelect().subscribe({
            next: (res) => {
                this.isCarregandoListasPreco = false;

                if (res.isError) return;

                this.listasPreco = res.list ?? [];

                // O documento carregado já tem a lista: o nome só existe depois do lookup.
                const idLista = this.listaPrecoControl.value;
                this.nomeListaPrecoAlterado.emit(this.listasPreco.find((l) => l.id === idLista)?.descricao ?? null);

                if (avisar) {
                    this.alertService.show(
                        'Listas de preço atualizadas',
                        this.listasPreco.length + ' disponíveis.',
                        AlertType.success,
                    );
                }
            },
            error: () => {
                this.isCarregandoListasPreco = false;
                this.listasPreco = [];
            },
        });
    }

    /**
     * §4.4: trocar a lista recarrega o preço de lista de todos os itens. Quem faz isso é a seção
     * de Itens — aqui só se anuncia a troca, porque esta seção não conhece item nenhum.
     */
    public aoTrocarListaPreco(): void {
        const idLista = this.listaPrecoControl.value;

        this.listaPrecoAlterada.emit(idLista);
        this.nomeListaPrecoAlterado.emit(this.listasPreco.find((l) => l.id === idLista)?.descricao ?? null);
    }

    /**
     * Máscara DD/MM/AAAA enquanto o usuário digita, no padrão do resto do sistema (o filtro de
     * Contas a Receber usa o mesmo MaskService). Sem ela, só o calendário serve — e teclar a data
     * é mais rápido do que navegar nele.
     */
    public aoDigitarData(evento: Event, campo: string): void {
        MaskService.onDateInput(evento, this.form.get(campo));
    }

}
